/**
 * `useActiveTenantId` — resolve the tenant id used to scope per-tenant
 * queries, in a way that works for BOTH Cerberus grant flavours.
 *
 * This lived in `modules/finance/hooks.ts` until 2026-08-24. It was misfiled:
 * its only dependencies are `useAuth`, `CERBERUS_ACCEPTED_DOMAINS` and
 * `type Grant` — all from this very folder. It is a Cerberus grant-resolution
 * helper, not a finance concern, and it is consumed by eight admin modules
 * (aegis, clio, decisioning, echo, kairos, kronos, pheme, proteus) that have
 * nothing to do with finance. Lifting it here is what lets the finance module
 * be deleted without taking the admin console down with it.
 *
 * Cerberus multi-tenant emits grants in two flavours:
 *   - B2B (team grant):     `{org_id, domain, tier?}`
 *   - B2C (personal grant): `{user_id, domain, tier?}`
 *
 * Resolution order:
 *   1. `activeOrgId` set (a B2B membership is selected) → return it.
 *   2. Authed user with an accepted-domain personal grant
 *      (`user_id === user.id`, no `org_id`) → return `user.id`.
 *   3. Else → `null` (truly no access; `<AuthGate>` already showed
 *      `<NoAccessScreen>` upstream, so this branch is only reachable in
 *      transient loading states or storybook).
 *
 * The function does NOT throw and does NOT redirect — consumers gate their
 * queries on `Boolean(tenant_id)`, and the route-level "no access" UX lives
 * in `<AuthGate>` / `<NoAccessScreen>`. The old throwing variant
 * (gambiarra-era, pre-#190) crashed ProfileView for personal-grant users
 * with empty memberships.
 */
// Importa do BARREL, não dos módulos-folha, de propósito: várias suítes
// mockam `@/lib/cerberus` inteiro para injetar um `useAuth` sintético e
// exercitar a resolução de tenant de verdade contra ele. Se este arquivo
// puxasse `./useAuth` direto, o mock do barrel não o interceptaria e a
// função rodaria contra o auth real, esvaziando o teste em silêncio.
// O ciclo index -> useActiveTenantId -> index é seguro porque os bindings
// só são lidos em tempo de render, dentro do corpo da função.
import { CERBERUS_ACCEPTED_DOMAINS, useAuth } from '.'
import type { Grant } from './types'

export function useActiveTenantId(): string | null {
  const { activeOrgId, status, user } = useAuth()
  if (activeOrgId) return activeOrgId
  if (status !== 'authed' || !user) return null
  const hasPersonalAcceptedGrant = user.grants.some(
    (g: Grant) =>
      !g.org_id &&
      g.user_id === user.id &&
      CERBERUS_ACCEPTED_DOMAINS.includes(g.domain),
  )
  if (hasPersonalAcceptedGrant) return user.id
  return null
}
